/**
 * Support Service — user-facing endpoints for the In-App Support widget.
 * See docs/analysis/support-help-center-plan.md Part 2 for the full contract.
 * Org Admin (`/support/admin/*`) and Super Admin (`/platform/support-requests/*`)
 * sections land with the queue pages (Task #4).
 */

import apiClient from './apiClient';
import { downloadSupportAttachment } from './supportAttachmentDownload';
import type {
  CreateSupportRequestInput,
  SupportReplyInput,
  SupportRequestDetail,
  SupportRequestStatus,
  SupportPaginatedResponse,
} from '@upskill/shared';

interface SupportRequestListEnvelope extends SupportPaginatedResponse {
  success: boolean;
}

export interface ListMyRequestsParams {
  page?: number;
  limit?: number;
  status?: SupportRequestStatus;
}

interface SupportUnreadCountResponse {
  success: boolean;
  data: { count: number };
}

const BASE = '/api/v1/support';

// Background requests skip the 401 → refresh → redirect cycle in the interceptor —
// unread-count polling should fail silently rather than force a logout.
const bgConfig = { _backgroundRequest: true } as any;

export const createSupportRequest = async (
  input: CreateSupportRequestInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<{ success: boolean; data: SupportRequestDetail }>(
    `${BASE}/requests`,
    input,
  );
  return response.data.data;
};

// Paginated like the two admin queues (SupportPaginatedResponse, shared type) —
// hooks/components see the paginated envelope's inner shape (`{data,total,...}`),
// never the outer `{success,...}` wire envelope.
export const getMyRequests = async (
  params: ListMyRequestsParams = {},
): Promise<SupportPaginatedResponse> => {
  const response = await apiClient.get<SupportRequestListEnvelope>(`${BASE}/requests`, { params });
  const { success: _success, ...paginated } = response.data;
  return paginated;
};

export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<SupportUnreadCountResponse>(
    `${BASE}/requests/unread-count`,
    bgConfig,
  );
  return response.data.data.count;
};

export const getRequestDetail = async (id: string): Promise<SupportRequestDetail> => {
  const response = await apiClient.get<{ success: boolean; data: SupportRequestDetail }>(
    `${BASE}/requests/${id}`,
  );
  return response.data.data;
};

export const replyToRequest = async (
  id: string,
  input: SupportReplyInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<{ success: boolean; data: SupportRequestDetail }>(
    `${BASE}/requests/${id}/messages`,
    input,
  );
  return response.data.data;
};

export const markRequestRead = async (id: string): Promise<void> => {
  await apiClient.post(`${BASE}/requests/${id}/read`);
};

export const rateCsat = async (id: string, rating: number): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<{ success: boolean; data: SupportRequestDetail }>(
    `${BASE}/requests/${id}/csat`,
    { rating },
  );
  return response.data.data;
};

export const downloadAttachment = downloadSupportAttachment;

const supportService = {
  createSupportRequest,
  getMyRequests,
  getUnreadCount,
  getRequestDetail,
  replyToRequest,
  markRequestRead,
  rateCsat,
  downloadAttachment,
};

export default supportService;
